import React from 'react';

import {
  CONVERSION_WINDOW_SIZE_LIMITS,
  CONVERSION_WINDOW_UNITS,
  MAX_HOLDING_PROPERTY_CONSTANT,
  REPORT_BASICS_LINKS,
  ReportName,
} from 'common/report/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {QueryBuilderUpdateDetail} from 'common/query-builder/query-builder/types';
import {
  EntrySectionUpdates,
  FlowsQueryEventEntry,
  QuerySuperEntry,
  RowLabelType,
  SelectorTypeDefinitions,
} from 'common/query-builder/query-entry-section/types';
import {ReportQueryBuilder} from 'common/report/report-manager/query-builder';
import {FLOWS_DEFAULT_EVENT, FlowsReport} from 'common/report/flows/models/report';
import {
  getEventSectionType,
  getQbOtherOptions,
  getQueryEventsSection,
  getSelectedConversionOptions,
  getUniqueEventNames,
  updateReportFromConversionOptions,
  updateReportFromQBSelection,
} from 'common/report/flows/util';
import {ChartType, FlowsBookmark} from 'common/report/flows/models/types';
import {SearchDisplayContext} from 'common/report/util/search/types';
import {ConversionQueryOptionsReact} from 'common/query-builder/conversion-query-options';
import {QueryOptionsSelection} from 'common/query-builder/conversion-query-options/types';
import {addToast} from 'common/components/toast-area/toast-manager';
import {CountType} from 'common/report/funnels/models/types';
import {emitTrackEvent} from 'common/util/dom';
import {QueryReason} from 'common/report/util/query-tracking/types';
import {FlowsResponse} from 'common/report/funnels/models/api-types';
import {QueryEntrySectionReact} from 'common/query-builder/query-entry-section';

import {TrackingEvent} from '../tracking';

class FlowsQueryBuilder extends ReportQueryBuilder<FlowsBookmark, FlowsResponse, EmptyObj> {
  displayContext = SearchDisplayContext.Flows;
  reportType = ReportName.Flows;

  bookmarkUpdateFromQbUpdate(updateDetail: QueryBuilderUpdateDetail<FlowsQueryEventEntry>): FlowsBookmark {
    return updateReportFromQBSelection(this.getReportModel(), updateDetail).generateBookmark();
  }

  qbChildrenTemplate() {
    if (!this.params.bookmarkParams || !this.params.project) {
      return null;
    }
    const report = this.getReportModel();
    const {t} = this.getContext(`i18n`);

    return (
      <div className='body-section'>
        <ConversionQueryOptionsReact
          eventNames={getUniqueEventNames(report)}
          projectId={this.params.projectId}
          selected={getSelectedConversionOptions(report)}
          showSessions
          workspaceId={this.params.workspaceId}
          builderParams={{
            exclusionSteps: report.exclusionSteps,
            entries: getQueryEventsSection(report),
            dataGroupsEnabled: this.canUseDataGroups(),
            projectId: this.params.projectId,
            workspaceId: this.params.workspaceId,
            dataGroupId: report.dataGroupId || ``,
            projectHasPeople: this.params.project.hasIntegratedEngage,
            projectHasCohorts: this.canUseCohorts(),
            trackName: this.reportType,
            projectAsOfDate: this.params.project.projectAsOfDate,
            entryRowLabelType: RowLabelType.Alpha,
          }}
          maxHoldingPropertyConstant={MAX_HOLDING_PROPERTY_CONSTANT}
          conversionWindowUnits={CONVERSION_WINDOW_UNITS}
          conversionWindowSizeLimits={CONVERSION_WINDOW_SIZE_LIMITS}
          hideAnyOrder
          hideConversionWindow={report.events.length < 2}
          enableStickyHeader
          onChangeQueryOption={(options) => this.onConversionQueryOptionsChange(options)}
          onTrack={(data) => emitTrackEvent(this, {eventName: data.eventName, props: data.properties})}
        />
        <QueryEntrySectionReact
          elref='propertyExpansionBlock'
          addEntryButtonTooltipText='Zoom in on specific details about each step in the flow.'
          dataGroupId={report.dataGroupId || ``}
          includeCustomProperties='all'
          showBorrowedProperties
          projectId={this.params.projectId}
          rowTypes={[SelectorTypeDefinitions.PropertyExpansion]}
          entries={
            report.segments.map((item) => ({
              filters: [],
              event: item,
              type: SelectorTypeDefinitions.PropertyExpansion,
            })) as unknown as QuerySuperEntry[]
          } // TODO: fix QuerySuperEntry type to allow this
          enableStickyHeader
          sectionTitle={t(`flows.expand-events-by-prop`)}
          workspaceId={this.params.workspaceId}
          onUpdate={(updates) => this.onPropertyExpansionChange(updates)}
        />
      </div>
    );
  }

  onConversionQueryOptionsChange(options: QueryOptionsSelection) {
    const report = this.getReportModel();

    if ([CountType.Unique, CountType.General].includes(report.countType) && options.countType === CountType.Session) {
      addToast({message: `Counting Sessions requires a conversion window of 1 session`});
    }

    if (report.funnelOrder !== options.funnelOrder) {
      emitTrackEvent(TrackingEvent.ANY_ORDER_TOGGLE);
    }

    const updatedReport = updateReportFromConversionOptions(report, options);
    this.dispatchUpdate({params: updatedReport.generateBookmark(), queryReason: QueryReason.QbOtherUpdate});
  }

  onPropertyExpansionChange(updates: EntrySectionUpdates<any>) {
    const segments = updates.entries.map((entry) => entry.event);
    const report = this.getReportModel();
    report.update({segments});
    this.dispatchUpdate({params: report.generateBookmark(), queryReason: QueryReason.QbOtherUpdate});
  }

  reportSpecificQBParams() {
    const report = this.getReportModel();

    return {
      addEntryButtonTooltipText: `Select an event to see the most common things users do before/after the event.\n[Read Intro to Flows](${
        REPORT_BASICS_LINKS[ReportName.Flows]
      })`,
      breakdownResourceTypes: [`cohort`],
      dataGroupId: report.dataGroupId || ``,
      dateRange: report.dateRange.getAttributes(),
      disableBreakdown: report.chartType === ChartType.Paths,
      enableEnhancedListFilters: true,
      entries: getQueryEventsSection(report),
      entriesDraggable: !report.containsFunnelBookmark() ? true : false,
      exclusionSteps: report.exclusionSteps,
      filters: report.filters,
      groupBy: report.groupBy,
      groupByLimitation: 1,
      includeCustomProperties: `all`,
      showBorrowedProperties: true,
      headerTitle: `Steps`,
      hideDatePicker: true,
      rowLimit: 0,
      entryRowLabelType: `alpha`,
      supportB2bCohorts: this.canUseDataGroups(),
      rowTypes: [getEventSectionType()],
      otherGroupByOptions: getQbOtherOptions(),
      otherFilterOptions: getQbOtherOptions(),
      minEntries: 1,
      emptyPlaceholder: FLOWS_DEFAULT_EVENT as QuerySuperEntry,
      ...(report.isAnyOrderFunnel() ? {entryIndexToggleIcon: `any-order`, entryIndexToggleable: true} : {}),
      ...(report.containsFunnelBookmark() ? {hideDataGroupsSelector: true, readonlyEntries: true} : {}),
    };
  }

  getReportModel() {
    return FlowsReport.fromBookmarkParams(this.params.bookmarkParams);
  }
}

export const FlowsQueryBuilderEl = defineAndMakeJsxFactory(`flows-query-builder`, FlowsQueryBuilder);
export const FlowsQueryBuilderReact = panel2react(`flows-query-builder`, FlowsQueryBuilder);
